import { useVendor } from '@outdoorly/react-chat-sdk';
import React, { useEffect } from 'react';
import { Store } from '../store';
import { CONFIG_DEFAULTS } from '../constants';

const LabelVariable = {
  // Opting for 'brand' over 'vendor' here since these will be customer facing
  brandName: 'brandName',
  expertName: 'expertName',
  rating: 'rating',
  firstName: 'firstName'
};

type Entries<T> = {
  [K in keyof T]: [K, T[K]];
}[keyof T][];

type LabelConfig = typeof CONFIG_DEFAULTS.labels;
type LabelVariable = keyof typeof LabelVariable;
export type LabelVariableSubstitutions = Partial<Record<LabelVariable, string>>;

type ReservedKeys = {
  // Our label config must not define keys that conflict with the properties
  // which SubstitutableLabels defines.
  [ReservedKey in keyof SubstitutableLabelsProperties]?: never;
};

type RecursiveDictValue<T> = T | RecursiveDict<T>;
type RecursiveDict<T> = {
  [key: string]: RecursiveDictValue<T>;
};

type LabelDictValue = RecursiveDictValue<string>;
type LabelDict = RecursiveDict<string>;

type TemplateDict = RecursiveDict<Template>;
type TemplateDictValue = RecursiveDictValue<Template>;

type MappedLabels<Type extends RecursiveDict<Source> & ReservedKeys, Source, Target> = {
  [Property in keyof Type]: Type[Property] extends RecursiveDict<Source>
    ? MappedLabels<Type[Property], Source, Target>
    : Target;
};

export const substituteLabelVariables = (label: string, variables: LabelVariableSubstitutions = {}) => {
  const providedVariables = Object.keys(variables) as Array<keyof typeof variables>;

  return providedVariables.reduce(
    (acc, curr) => acc.replace(new RegExp(`{{${curr}}}`, 'g'), variables[curr]!),
    label
  );
};

class Template extends String {
  constructor(template: string) {
    super(template);
  }

  substitute(this: Template, variables: Parameters<typeof substituteLabelVariables>[1]) {
    return substituteLabelVariables(this.valueOf(), variables);
  }
}

type SubstitutableLabelsProperties = {
  substitute: (substitutions: LabelVariableSubstitutions) => SubstitutableLabels;
  substitutions: LabelVariableSubstitutions;
  templates: MappedLabels<LabelConfig, string, Template>;
};

const RESERVED_KEYS = ['substitute', 'substitutions', 'templates'];

export type SubstitutableLabels = LabelConfig & SubstitutableLabelsProperties;

function isTemplate(value: TemplateDictValue): value is Template {
  return (value as Template).substitute !== undefined;
}

function recursiveSubstitute<T extends TemplateDict>(
  value: T,
  substitutions: LabelVariableSubstitutions
): MappedLabels<T, Template, string> {
  return Object.entries(value).reduce((acc, [key, value]) => {
    if (isTemplate(value)) {
      acc[key] = value.substitute(substitutions);
      return acc;
    }

    acc[key] = recursiveSubstitute(value, substitutions);
    return acc;
  }, {} as LabelDict) as MappedLabels<T, Template, string>;
}

/**
 * Calling .substitute on the returned object will return a new SubstitutableLabels with the substitutions applied (overriding any prior substitutions).
 * @returns a SubstitutableLabels with the given substitutions applied.
 */
function getLabels(
  this: Pick<SubstitutableLabels, 'templates' | 'substitutions'>,
  substitutions: LabelVariableSubstitutions = {}
): SubstitutableLabels {
  const labels = this;
  const priorSubstitutions = labels.substitutions || {};
  const allSubstitutions = { ...priorSubstitutions, ...substitutions };

  const substitutedLabels = {
    ...recursiveSubstitute(labels.templates, allSubstitutions),
    templates: labels.templates,
    substitutions: allSubstitutions
  };

  Object.defineProperty(substitutedLabels, 'substitute', {
    value: getLabels.bind(substitutedLabels)
  });

  return substitutedLabels as SubstitutableLabels;
}

function recursiveAsValueTemplatePair<T extends LabelDictValue>(
  labelValue: T
): [T extends LabelDict ? T : string, T extends LabelDict ? MappedLabels<T, string, Template> : Template];
function recursiveAsValueTemplatePair(
  labelValue: string | LabelDict
): [string, Template] | [LabelDict, TemplateDict] {
  if (typeof labelValue === 'string') {
    const template = new Template(labelValue);
    return [template.valueOf(), template];
  }

  const values: LabelDict = {};
  const templates: TemplateDict = {};

  Object.entries(labelValue).forEach(([key, value]) => {
    const [valueOf, template] = recursiveAsValueTemplatePair(value);
    templates[key] = template;
    values[key] = valueOf;
  });

  return [values, templates];
}

/**
 * Helper function for turning plain labels into SubstitutableLabels.
 */
function asSubstitutableLabels(labels: LabelConfig): SubstitutableLabels {
  const substitutableLabels = (Object.entries(labels) as Entries<LabelConfig>).reduce(
    (acc, curr) => {
      const [key, value] = curr;
      const [valueOf, template] = recursiveAsValueTemplatePair(value);
      acc[key] = valueOf;
      acc.templates[key] = template;
      return acc;
    },
    { templates: {}, substitutions: {} } as LabelDict & { templates: TemplateDict } & Pick<
        SubstitutableLabels,
        'substitutions'
      >
  );

  Object.defineProperty(substitutableLabels, 'substitute', {
    value: getLabels.bind(substitutableLabels as unknown as SubstitutableLabels)
  });

  return substitutableLabels as unknown as SubstitutableLabels;
}

/**
 * A component which triggers a side-effect to update
 * labels in the Store whenever the default label config or vendor updates
 */
export const LabelUpdater = React.memo(() => {
  const { vendor } = useVendor();
  const configLabels = Store.useState((s) => s.config?.labels ?? {});

  useEffect(() => {
    const vendorOverrides = Object.fromEntries(
      vendor?.labelOverrides.filter((o) => !RESERVED_KEYS.includes(o.key)).map((o) => [o.key, o.value]) ?? []
    );

    const withVendorOverrides = {
      ...configLabels,
      ...vendorOverrides
    };

    Store.update((s) => {
      s.labels = getLabels.bind(asSubstitutableLabels(withVendorOverrides))({
        brandName: vendor?.name
      });
    });
  }, [vendor, configLabels]);

  return null;
});

export const getDefaultLabels = getLabels.bind(asSubstitutableLabels(CONFIG_DEFAULTS.labels));
